/**
 * Offline Tax Engine — Applique les tax rules depuis RxDB local.
 * Lit les tenantTaxRules répliquées localement.
 * JAMAIS de taux hardcodé — tout vient de la DB locale.
 */

export interface TaxableItem {
  productId: string;
  familyId?: number;
  clientTypeId?: number;
  amount: number;
}

export interface TaxResult {
  subtotal: number;
  taxAmount: number;
  total: number;
  breakdown: TaxLineResult[];
}

export interface TaxLineResult {
  taxName: string;
  taxCode: string;
  rate: number;
  baseAmount: number;
  taxAmount: number;
  isExempt: boolean;
}

export interface LocalTaxRule {
  id: string;
  name: string;
  taxId?: number;
  priority: number;
  conditions?: Record<string, string | number | boolean | null>;
  applicableProductFamilyIds?: number[];
  applicableClientTypeIds?: number[];
  rateOverride?: number;
  isExemption: boolean;
  isActive: boolean;
}

export interface LocalTax {
  id: number;
  code: string;
  name: string;
  rate: number;
  taxCategory?: string;
  calculationOrder: number;
  taxOnTax: boolean;
  taxOnTaxRef?: string;
  isCompound: boolean;
  isActive: boolean;
}

export function calculateTaxes(
  items: TaxableItem[],
  taxRules: LocalTaxRule[],
  taxes: LocalTax[],
): TaxResult {
  const activeRules = taxRules
    .filter(r => r.isActive)
    .sort((a, b) => a.priority - b.priority);

  const activeTaxes = taxes
    .filter(t => t.isActive)
    .sort((a, b) => a.calculationOrder - b.calculationOrder);

  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const breakdown: TaxLineResult[] = [];
  let runningBase = subtotal;

  for (const tax of activeTaxes) {
    // Find applicable rule for this tax
    const rule = activeRules.find(r => r.taxId === tax.id);

    // Check if exempt
    const isExempt = rule?.isExemption ?? false;
    if (isExempt) {
      breakdown.push({
        taxName: tax.name,
        taxCode: tax.code,
        rate: 0,
        baseAmount: runningBase,
        taxAmount: 0,
        isExempt: true,
      });
      continue;
    }

    const effectiveRate = rule?.rateOverride ?? tax.rate;
    const base = tax.taxOnTax ? runningBase : subtotal;
    const taxAmount = base * (effectiveRate / 100);

    breakdown.push({
      taxName: tax.name,
      taxCode: tax.code,
      rate: effectiveRate,
      baseAmount: base,
      taxAmount,
      isExempt: false,
    });

    // Always accumulate into runningBase so subsequent taxOnTax taxes see prior taxes
    runningBase += taxAmount;
  }

  const totalTax = breakdown.reduce((sum, line) => sum + line.taxAmount, 0);

  return {
    subtotal,
    taxAmount: totalTax,
    total: subtotal + totalTax,
    breakdown,
  };
}
